// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/layout';
import Header from '../../../components/header';
import { AgGridReact } from 'ag-grid-react';
import Pagination from '../../../components/pagination';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import { PAGE_SIZE } from '../../../_common/constant';
import { useAssetsApi } from '../../../store/api/hooks/assetsApiHook';
import ReportFilterBar from '../../../components/ReportFilterBar';
import Button from '../../../components/elements/button';
import { downloadExcel } from '../../../_common/functions';
import toast from 'react-hot-toast';

function currencyCellRenderer(params: any) {
  const val = params.value;
  if (val == null) return '--';
  return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const SparePartsUsageReport: React.FC = () => {
  const assetsApi = useAssetsApi();
  const [rowData, setRowData] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [summary, setSummary] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const fetchData = useCallback((pageNum: number, filterParams: Record<string, any> = {}) => {
    setRowData(null);
    const params: any = { PageSize: PAGE_SIZE, PageNumber: pageNum };
    if (filterParams.facility)    params.FacilityIDs    = filterParams.facility;
    if (filterParams.subFacility) params.SubFacilityIDs = filterParams.subFacility;
    if (filterParams.assetGroup)  params.AssetGroupIDs  = filterParams.assetGroup;
    if (filterParams.department)  params.DepartmentIDs  = filterParams.department;
    if (filterParams.startDate)   params.StartDate      = filterParams.startDate;
    if (filterParams.endDate)     params.EndDate        = filterParams.endDate;

    assetsApi.getSparePartsUsageReport(
      params,
      (response: any) => {
        const d = response?.data || response;
        setRowData(d?.rows || []);
        setSummary(d?.summary);
        setTotalRecords(d?.totalRecord || 0);
        setFilterOptions([
          {
            key: 'facility', label: 'Facility',
            options: (d?.facilities || []).map((f: any) => ({ value: String(f.id), label: f.name })),
          },
          {
            key: 'subFacility', label: 'Sub Facility',
            options: (d?.subFacilities || []).filter((f: any) => f.name?.trim()).map((f: any) => ({ value: String(f.id), label: f.name })),
          },
          {
            key: 'assetGroup', label: 'Asset Group',
            options: (d?.assetGroups || []).map((f: any) => ({ value: String(f.id), label: f.name })),
          },
          {
            key: 'department', label: 'Department',
            options: (d?.departments || []).map((f: any) => ({ value: String(f.id), label: f.name })),
          },
        ]);
      },
      (error: any) => { console.error('Failed to fetch spare parts usage report:', error); setRowData([]); }
    );
  }, []);

  useEffect(() => { fetchData(currentPage, filters); }, [currentPage]);

  const handleApplyFilter = (filterMap: Record<string, string>, startDate?: string, endDate?: string) => {
    const newFilters = { ...filterMap, startDate, endDate };
    setFilters(newFilters);
    setCurrentPage(1);
    fetchData(1, newFilters);
  };

  const handleExport = () => {
    const params: any = { PageSize: null, PageNumber: null };
    if (filters.facility)    params.FacilityIDs    = filters.facility;
    if (filters.subFacility) params.SubFacilityIDs = filters.subFacility;
    if (filters.assetGroup)  params.AssetGroupIDs  = filters.assetGroup;
    if (filters.department)  params.DepartmentIDs  = filters.department;
    if (filters.startDate)   params.StartDate      = filters.startDate;
    if (filters.endDate)     params.EndDate        = filters.endDate;
    setIsExporting(true);
    assetsApi.exportSparePartsUsageReportToXLS(
      params,
      (resp: any) => { downloadExcel(resp, 'spare_parts_usage_report'); toast.success('Exported successfully!'); setIsExporting(false); },
      (error: any) => { toast.error(error || 'Export failed'); setIsExporting(false); }
    );
  };

  const columnDefs = [
    { headerName: 'Part Name',    field: 'partsName',      headerClass: 'bg-[#f0f0f0]', minWidth: 200, flex: 1, cellRenderer: titleCell, headerTooltip: 'Part Name' },
    { headerName: 'Part Code',    field: 'partsCode',      headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: titleCell, headerTooltip: 'Part Code' },
    { headerName: 'Facility',     field: 'facilityName',   headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Facility' },
    { headerName: 'Sub Facility', field: 'subFacilityName',headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Sub Facility' },
    { headerName: 'Asset Group',  field: 'assetGroupName', headerClass: 'bg-[#f0f0f0]', width: 150, cellRenderer: titleCell, headerTooltip: 'Asset Group' },
    { headerName: 'Department',   field: 'departmentName', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Department' },
    { headerName: 'Date Used',    field: 'dateUsed',       headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: titleCell, headerTooltip: 'Date Used' },
    { headerName: 'Qty Used',     field: 'usedQty',        headerClass: 'bg-[#f0f0f0]', width: 110, type: 'rightAligned', headerTooltip: 'Quantity Used' },
    { headerName: 'Unit Price',   field: 'unitPrice',      headerClass: 'bg-[#f0f0f0]', width: 120, type: 'rightAligned', cellRenderer: currencyCellRenderer, headerTooltip: 'Unit Price' },
    { headerName: 'Net Total',    field: 'netTotal',       headerClass: 'bg-[#f0f0f0]', width: 130, type: 'rightAligned', cellRenderer: currencyCellRenderer, headerTooltip: 'Net Total' },
    { headerName: 'Work Order',   field: 'workOrderCode',  headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Work Order' },
    { headerName: 'Asset Name',   field: 'assetName',      headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Asset Name' },
  ];

  return (
    <Layout>
      <Header title="Spare Parts Usage Report" back="/reports">
        <Header.Right>
          <Button theme='primary' onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </Header.Right>
      </Header>
      <div className="p-4 pb-0">
        <ReportFilterBar availableFilters={filterOptions} onApply={handleApplyFilter} />
        {summary && (
          <div className="bg-white rounded-lg mb-4 flex items-center gap-6 flex-wrap text-[13px]" style={{ padding: '12px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <span style={{ color: '#111827', fontWeight: 600 }}>
              Total Parts Used: {Number(summary.totalQtyUsed || 0).toLocaleString('en-US')} units
            </span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#2B5A2E', fontWeight: 600 }}>
              Total Value: ${Number(summary.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}
      </div>
      <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
        <Pagination perPage={PAGE_SIZE} totalRecords={totalRecords} onPageChange={setCurrentPage} currentPage={currentPage} />
      </div>
      <div className='bg-[#FAFAFA] w-full'>
        <div className="ag-theme-alpine px-4 w-full h-1/2">
          <AgGridReact
            columnDefs={columnDefs as any}
            rowData={rowData || []}
            rowSelection="multiple"
            pagination={false}
            domLayout="autoHeight"
            loadingOverlayComponent={Loader}
            noRowsOverlayComponent={() => (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">No Data Found</div>
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </Layout>
  );
};

export default SparePartsUsageReport;
